import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PostService, Post } from '../../services/post';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MetaService } from '../../services/meta.service';

@Component({
	selector: 'app-post-detail',
	imports: [CommonModule, RouterLink],
	templateUrl: './post-detail.html',
	styleUrl: './post-detail.css',
	standalone: true,
})
export class PostDetail implements OnInit {
	private blogService = inject(PostService);
	private route = inject(ActivatedRoute);
	private sanitizer = inject(DomSanitizer);
	private metaService = inject(MetaService);

	blog = signal<Post | null>(null);
	relatedBlogs = signal<Post[]>([]);
	isLoading = signal<boolean>(true);
	errorMessage = signal<string>('');

	ngOnInit() {
		this.route.params.subscribe((params) => {
			const slug = params['slug'];
			if (slug) {
				this.loadBlog(slug);
			}
		});
	}

	loadBlog(slug: string) {
		this.isLoading.set(true);
		this.errorMessage.set('');

		this.blogService.getBlogBySlug(slug).subscribe({
			next: (response) => {
				if (response.status && response.data) {
					this.blog.set(response.data);

					// Set dynamic meta tags for the blog post
					const title = `${response.data.title} | OG Studio Blog`;
					const description = response.data.short_description || response.data.title;
					this.metaService.setCustomMeta(title, description);

					this.trackView(response.data.id, slug);
					this.loadRelatedBlogs();
				} else {
					this.errorMessage.set('Blog post not found');
				}
				this.isLoading.set(false);
			},
			error: (error) => {
				console.error('Error loading blog:', error);
				this.errorMessage.set('Failed to load blog post');
				this.isLoading.set(false);
			},
		});
	}

	trackView(blogId: number, slug: string) {
		this.blogService.trackView(blogId).subscribe({
			next: () => {
				// Reload blog data to get updated view count
				this.blogService.getBlogBySlug(slug).subscribe({
					next: (response) => {
						if (response.status && response.data) {
							this.blog.set(response.data);
						}
					},
				});
			},
		});
	}

	loadRelatedBlogs() {
		this.blogService.getPublishedBlogs({ limit: 3 }).subscribe({
			next: (response) => {
				if (response.status) {
					// Filter out current blog
					const currentBlogId = this.blog()?.id;
					this.relatedBlogs.set(
						response.data.filter((b: Post) => b.id !== currentBlogId).slice(0, 3),
					);
				}
			},
			error: (error) => console.error('Error loading related blogs:', error),
		});
	}

	getSafeHtml(content: string): SafeHtml {
		return this.sanitizer.sanitize(1, content) || '';
	}

	getImageUrl(path: string): string {
		return path || '/images/placeholder-blog.jpg';
	}

	formatDate(date: string): string {
		return new Date(date).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	}

	onImageError(event: Event) {
		const img = event.target as HTMLImageElement;
		if (!img.src.includes('data:image')) {
			img.src =
				'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect fill="%23eaeae7" width="400" height="300"/%3E%3Ctext fill="%235d202d" font-family="system-ui" font-size="18" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
			img.onerror = null;
		}
	}

	shareOn(platform: string) {
		const url = encodeURIComponent(window.location.href);
		const title = encodeURIComponent(this.blog()?.title || '');
		let shareUrl = '';

		switch (platform) {
			case 'facebook':
				shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
				break;
			case 'twitter':
				shareUrl = `https://x.com/intent/tweet?url=${url}&text=${title}`;
				break;
			case 'linkedin':
				shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
				break;
			case 'whatsapp':
				shareUrl = `https://wa.me/?text=${title}%20${url}`;
				break;
			case 'instagram':
				// Instagram doesn't support direct URL sharing, so we'll open Instagram
				shareUrl = 'https://www.instagram.com/';
				break;
		}

		if (shareUrl) {
			window.open(shareUrl, '_blank', 'width=600,height=400');
		}
	}

	linkCopied = signal(false);

	copyLink() {
		navigator.clipboard.writeText(window.location.href).then(() => {
			this.linkCopied.set(true);
			setTimeout(() => this.linkCopied.set(false), 2000);
		});
	}
}
